
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */
import Img from 'theme/components/shared/Img'


const layoutProps = {
  
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">

    <h1 {...{
      "id": "classification-propagation"
    }}>{`Classification Propagation`}</h1>
    <ul>
      <li parentName="ul">{`Classification propagation enables classifications associated to an entity to be automatically associated with other related entities of the entity. This is very useful in dealing with scenarios where a dataset derives it data from other datasets - like a table loaded with data in a file, a report generated from a table/view, etc.`}</li>
      <li parentName="ul">{`For example, when a table is classified as `}<em parentName="li">{`PII`}</em>{`, tables or views that derive data from this table (via CTAS or ‘create view’ operation) will be automatically classified as `}<em parentName="li">{`PII`}</em>{`.`}</li>
    </ul>
    <h2 {...{
      "id": "use-cases"
    }}>{`Use Cases`}</h2>
    <p>{`Consider the following lineage where data from a 'hdfs_path' entity is loaded into a table, which is further made available through views. We will go through various scenarios to understand the classification propagation feature.`}</p>
    <Img src={`/images/twiki/classification-propagation-1.png`} mdxType="Img" />
    <h2 {...{
      "id": "add-classification-to-an-entity"
    }}>{`Add classification to an entity`}</h2>
    <p>{`When classification ‘PII’ is added to 'hdfs_path' entity, the classification is propagated to all impacted entities in the lineage path, including 'employees' table, views 'us_employees' and 'uk_employees' - as shown below.`}</p>
    <Img src={`/images/twiki/classification-propagation-2.png`} mdxType="Img" />
    <h2 {...{
      "id": "update-classification-associated-with-an-entity"
    }}>{`Update classification associated with an entity`}</h2>
    <p>{`Any updates to classifications associated with an entity will be seen in all entities the classification is propagated to as well.`}</p>
    <Img src={`/images/twiki/classification-propagation-3.png`} mdxType="Img" />
    <h2 {...{
      "id": "remove-classification-associated-with-an-entity"
    }}>{`Remove classification associated with an entity`}</h2>
    <p>{`When a classification is deleted from an entity, the classification will be removed from all entities the classification is propagated to as well.`}</p>
    <Img src={`/images/twiki/classification-propagation-4.png`} mdxType="Img" />
    <h2 {...{
      "id": "add-lineage-between-entities"
    }}>{`Add lineage between entities`}</h2>
    <p>{`When lineage is added between entities, for example to capture loading of data in a file to a table, the classifications associated with the source entity are propagated to all impacted entities as well.
For example, when a view is created from a table, classifications associated with the table are propagated to the newly created view as well.`}</p>
    <Img src={`/images/twiki/classification-propagation-5.png`} mdxType="Img" />
    <h2 {...{
      "id": "delete-an-entity"
    }}>{`Delete an entity`}</h2>
    <p><strong parentName="p">{`Case 1:`}</strong>{`
When an entity is deleted, classifications associated with this entity will be removed from all entities the classifications are propagated to.
For example. when `}<em parentName="p">{`employees`}</em>{` table is deleted, classifications associated with this table are removed from 'employees_view' view.`}</p>
    <Img src={`/images/twiki/classification-propagation-6.png`} mdxType="Img" />
    <p><strong parentName="p">{`Case 2:`}</strong>{`
When an entity is deleted in the middle of a lineage path, the propagation link is broken and previously propagated classifications will be removed from all derived entities of the deleted entity.
For example. when 'us_employees' table is deleted, classifications propagating through this table (`}<strong parentName="p">{`PII`}</strong>{`) are removed from 'ca_employees' table, since the only path of propagation is broken by entity deletion.`}</p>
    <Img src={`/images/twiki/classification-propagation-entity-delete-1.png`} mdxType="Img" />
    <Img src={`/images/twiki/classification-propagation-entity-delete-2.png`} mdxType="Img" />
    <p><strong parentName="p">{`Case 3:`}</strong>{`
When an entity is deleted in the middle of a lineage path and if there exists alternate path for propagation, previously propagated classifications will be retained.
For example. when 'us_employees' table is deleted, classifications  propagating (`}<strong parentName="p">{`PII`}</strong>{`) through this table are retained in 'ca_employees' table, since there are two propagation paths available and only one of them is broken by entity deletion.`}</p>
    <Img src={`/images/twiki/classification-propagation-entity-delete-3.png`} mdxType="Img" />
    <Img src={`/images/twiki/classification-propagation-entity-delete-4.png`} mdxType="Img" />
    <h2 {...{
      "id": "control-propagation"
    }}>{`Control Propagation`}</h2>
    <p>{`Apache Atlas provides few options to control whether/where a classification is propagated.
This section details available options.`}</p>
    <h2 {...{
      "id": "propagate-flag-in-classification"
    }}>{`Propagate flag in classification`}</h2>
    <p>{`Each association of classification to an entity has a boolean flag that controls whether the classification is propagated or not.
When a classification is associated with an entity, this flag is set to ‘true’ i.e. the classification will be propagated to all impacted entities. This flag can be updated to desired value during initial association or later.`}</p>
    <Img src={`/images/twiki/classification-propagation-7.png`} mdxType="Img" />
    <h2 {...{
      "id": "propagate-flag-in-lineage-edge"
    }}>{`Propagate flag in lineage edge`}</h2>
    <p>{`Apache Atlas supports a flag at lineage edge to enable/disable propagation of classifications through the edge. By default, the propagation is enabled for lineage edges.
When the flag is turned off, no classification will be propagated through this edge; and propagation of currently propagated classifications through the edge will be reevaluated, so that they can be removed from impacted entities.
When the flag is turned on, propagation of classifications of the source entity will be reevaluated, so that they can be propagated to all impacted entities.`}</p>
    <h2 {...{
      "id": "block-propagation-of-specific-classifications-in-lineage-edge"
    }}>{`Block propagation of specific classifications in lineage edge`}</h2>
    <p>{`Apache Atlas supports blocking propagation of specific classifications in at lineage edges.
This can be useful, for example, to handle scenarios like: a column classified as PII is masked when creating a view; in such scenario, if corresponding column in the created view might not have PII, hence the propagation of PII classification should be blocked.
This can be done by updating the lineage edge to add the PII classification in `}<em parentName="p">{`‘blocked propagated classifications’`}</em>{` list.
Classifications in blocked propagated classifications will not be propagated in the derivative/downstream entities.`}</p>
    <Img src={`/images/twiki/classification-propagation-8.png`} mdxType="Img" />
    <h2 {...{
      "id": "notifications-and-audit"
    }}>{`Notifications and Audit`}</h2>
    <p>{`When propagated classifications are added/update/deleted, Apache Atlas sends notifications to 'ATLAS_ENTITIES' topic for each entity affected by the propagation.`}</p>
    <h2 {...{
      "id": "glossary"
    }}>{`Glossary`}</h2>
    <p>{`When a classification is associated with a glossary term, the classification is automatically propagated to all entities associated with the term.`}</p>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;